import { useEffect, useState } from 'react';
import { useNotify, useRecordContext, useRedirect } from 'react-admin';
import { Box, Button, TextField } from '@mui/material';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { viaWriteStatusLabel } from '../../shared/status';
import { ALERT_BUTTON_SX, OrderAlertCard } from './OrderAlertCard';
import { toErrorMessage } from './orderActionsApi';
import { recordScoped, useStillMounted } from '../../shared/recordScoped';
import { useHasPermission } from '../PermissionGate';
import { useFeature } from '../featureFlags/FeatureGate';
import { SOURCE_TMS } from './recordSource';
import {
  FEATURE_PRE_ORDER_TMS_LINK,
  MIN_LINK_REASON_LENGTH,
  fetchPreOrderLink,
  linkPreOrder,
  type PreOrderLinkCandidate,
  type PreOrderLinkState,
} from './preOrderLinkApi';

/**
 * Zlecenie z TMS, które może pochodzić z naszego prezlecenia (15.10.2026,
 * pętla zwrotna z TMS; reguła w API: `via/pre-order-link.ts`).
 *
 * Import łączy sam tylko wtedy, gdy TMS odda nasz numer zlecenia. Bez niego
 * nie zgadujemy — tu człowiek z przyjęcia zleceń (CS, Administracja) widzi
 * prezlecenia z tym samym dniem załadunku i rozładunku i decyduje. Za
 * przełącznikiem `preOrderTmsManualLink`; bez niego możliwe połączenie
 * widać tylko w historii zlecenia.
 */

export const PRE_ORDER_LINK_TITLE = 'To zlecenie z TMS może pochodzić z prezlecenia';

export const PRE_ORDER_LINK_ORIGIN =
  'Prezlecenie po akceptacji ma nasz numer (MAR/…) i własny wiersz w banku. Gdy zlecenie trafia ' +
  'do TMS, import szuka tego numeru w numerach zlecenia z TMS i wtedy łączy wiersze sam. Tutaj ' +
  'TMS go nie oddał, ale spółka, dzień załadunku i rozładunku, kraje i miasta się zgadzają — ' +
  'czy to ten sam transport, decyduje człowiek.';

// Połączenie ręczne domyka zapis jak import (decyzja użytkownika 16.10.2026):
// „Jest w TMS” i alert przy planowanym wierszu.
export const PRE_ORDER_LINK_NOTE =
  'Po połączeniu wiersz z prezlecenia przejmuje numer, dane i punkty trasy z TMS, z historią ' +
  'i wariantami. Ten wiersz zostanie usunięty z wpisem, dokąd poszły dane. Zapis do VIA ' +
  'przejdzie w „Jest w TMS” — pliku już nie wyślemy, byłby w TMS duplikatem. Jeśli wiersz ' +
  'jest obsadzony albo ma warianty, a TMS różni się od prezlecenia (miejsca, terminy, waga), ' +
  'planista dostanie alert o zmianie w TMS.';

/** Wyraźna linia: które prezlecenie i jaka trasa. */
export function candidateText(c: PreOrderLinkCandidate): string {
  const from = c.fromCity ?? c.fromCountry ?? '—';
  const to = c.toCity ?? c.toCountry ?? '—';
  return `Prezlecenie ${c.orderNumber ?? `#${c.userOfferId}`}: ${from} → ${to}`;
}

/** Szara linia: terminy i czy plik wyszedł do VIA. */
export function candidateDetail(c: PreOrderLinkCandidate): string {
  const label = viaWriteStatusLabel(c.viaWriteStatus);
  const via = c.sentToVia
    ? `zapis do VIA: ${label.charAt(0).toLowerCase()}${label.slice(1)}`
    : 'pliku do VIA nie wysłano';
  return `Załadunek ${dateTimeNoSeconds(c.fromDate)}, rozładunek ${dateTimeNoSeconds(c.toDate)}; ${via}`;
}

type Target = { preOrderRowId: number; label: string } | { orderNumber: string; label: string };

const PreOrderLinkCard = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const redirect = useRedirect();
  const stillMounted = useStillMounted();
  const enabled = useFeature(FEATURE_PRE_ORDER_TMS_LINK);
  const canLink = useHasPermission('ORDER_INTAKE');
  const [state, setState] = useState<PreOrderLinkState | null>(null);
  const [target, setTarget] = useState<Target | null>(null);
  const [number, setNumber] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const id = record?.id;
  const applies =
    enabled && canLink && id !== undefined && id !== null && record?.sourceSystem === SOURCE_TMS && !record?.deleted;

  useEffect(() => {
    if (!applies) return;
    fetchPreOrderLink(id as number)
      .then((s) => {
        if (stillMounted()) setState(s);
      })
      // Bez listy nie ma czego pokazać — karta zlecenia działa dalej.
      .catch(() => undefined);
  }, [applies, id, stillMounted]);

  if (!applies || !state || state.candidates.length === 0) return null;

  const back = () => {
    setTarget(null);
    setReason('');
  };
  const reasonTooShort = reason.trim().length < MIN_LINK_REASON_LENGTH;
  const confirm = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const result = await linkPreOrder(
        id as number,
        'preOrderRowId' in target ? { preOrderRowId: target.preOrderRowId } : { orderNumber: target.orderNumber },
        reason,
      );
      notify(`Połączono. Zlecenie ${result.externalId ?? ''} jest teraz w wierszu prezlecenia.`, {
        type: 'success',
      });
      redirect('show', 'user-offers', result.id);
    } catch (err) {
      notify(toErrorMessage(err), { type: 'error' });
    } finally {
      if (stillMounted()) setBusy(false);
    }
  };

  let actions = null;
  if (state.refusal === null && target === null) {
    actions = (
      <>
        {state.candidates.map((c) => (
          <Button
            key={c.userOfferId}
            variant="outlined"
            sx={ALERT_BUTTON_SX}
            onClick={() =>
              setTarget({ preOrderRowId: c.userOfferId, label: c.orderNumber ?? `#${c.userOfferId}` })
            }
          >
            Połącz z {c.orderNumber ?? `#${c.userOfferId}`}
          </Button>
        ))}
      </>
    );
  } else if (target !== null) {
    actions = (
      <>
        <Button color="inherit" sx={ALERT_BUTTON_SX} disabled={busy} onClick={back}>
          Wróć
        </Button>
        <Button
          variant="contained"
          disableElevation
          sx={ALERT_BUTTON_SX}
          disabled={busy || reasonTooShort}
          onClick={() => void confirm()}
        >
          Połącz z {target.label}
        </Button>
      </>
    );
  }

  return (
    <OrderAlertCard
      tone="warning"
      title={PRE_ORDER_LINK_TITLE}
      detectedAt={[]}
      origin={PRE_ORDER_LINK_ORIGIN}
      lines={state.candidates.map((c) => ({
        id: c.userOfferId,
        text: candidateText(c),
        detail: candidateDetail(c),
      }))}
      note={state.refusal ?? PRE_ORDER_LINK_NOTE}
      actions={actions}
    >
      {state.refusal === null && target === null && (
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            label="Inne prezlecenie — nasz numer (MAR/…)"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            size="small"
            sx={{ minWidth: 260 }}
          />
          <Button
            variant="outlined"
            sx={ALERT_BUTTON_SX}
            disabled={number.trim() === ''}
            onClick={() => setTarget({ orderNumber: number.trim(), label: number.trim().toUpperCase() })}
          >
            Połącz z tym numerem
          </Button>
        </Box>
      )}
      {target !== null && (
        <Box sx={{ mt: 1.5 }}>
          <TextField
            label="Powód połączenia"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            size="small"
            autoFocus
            sx={{ width: '100%' }}
            helperText={`Co najmniej ${MIN_LINK_REASON_LENGTH} znaków — np. skąd wiesz, że to ten sam transport. Zapiszemy go w historii obu wierszy.`}
          />
        </Box>
      )}
    </OrderAlertCard>
  );
};

/** Stan należy do rekordu — inna karta zaczyna od zera (`recordScoped`). */
export const PreOrderLinkAlert = recordScoped(PreOrderLinkCard);
